import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

type CopyLinkButtonProps = Readonly<{
  url: string;
  ariaLabel?: string;
  className?: string;
}>;

// How long the "Copied!" confirmation stays visible after a successful copy.
const COPIED_FEEDBACK_MS = 1200;

// Standard visually-hidden style so the copy result is announced to screen
// readers without affecting layout.
const visuallyHidden: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API can be unavailable (insecure context) or blocked; fall back.
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

export function CopyLinkButton({ url, ariaLabel = 'Copy link', className }: CopyLinkButtonProps) {
  const [copied, setCopied] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const clearCopied = () => {
    setCopied(false);
    setIsTooltipOpen(false);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  };

  const handlePress = async () => {
    if (copied) {
      clearCopied();
      return;
    }
    const ok = await copyToClipboard(url);
    if (!ok) {
      // Don't show a false success; leave affordance unchanged.
      return;
    }
    setCopied(true);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(clearCopied, COPIED_FEEDBACK_MS);
  };

  return (
    <>
      {/* Force the tooltip open while `copied` so the confirmation is reliably
          visible — react-aria dismisses tooltips on press, which is why the
          previous hover-only "Copied" text showed inconsistently. */}
      <TooltipTrigger isOpen={isTooltipOpen || copied} onOpenChange={setIsTooltipOpen}>
        <IconButton
          icon={copied ? 'check' : 'link'}
          variant="minimal"
          aria-label={ariaLabel}
          onPress={handlePress}
          className={className}
        />
        <Tooltip placement="top" variant="popover">
          {copied ? (
            <span
              role="button"
              tabIndex={0}
              onClick={clearCopied}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  clearCopied();
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              Copied!
            </span>
          ) : (
            'Copy link'
          )}
        </Tooltip>
      </TooltipTrigger>
      <span role="status" aria-live="polite" style={visuallyHidden}>
        {copied ? 'Link copied to clipboard' : ''}
      </span>
    </>
  );
}
